import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Headphones, Download, ExternalLink, Loader2, Check } from "lucide-react";

const REFERRAL_URL = "https://elevenreader.io/referral/MK6ZD1kq";

export default function ElevenReaderDialog({ open, onOpenChange, onDownloadEpub }) {
  const [downloading, setDownloading] = useState(false);
  const [done, setDone] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await onDownloadEpub();
      setDone(true);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="elevenreader-dialog">
        <DialogHeader>
          <div className="w-11 h-11 rounded-xl bg-primary/15 grid place-items-center mb-2">
            <Headphones className="w-5 h-5 text-primary" />
          </div>
          <DialogTitle className="text-xl">Listen on ElevenReader</DialogTitle>
          <DialogDescription className="leading-relaxed">
            Turn your book into an audiobook on ElevenReader — natural AI narration you can
            play on any device. <span className="text-foreground font-medium">30 hours free</span> every
            month, or go unlimited with Ultra for ~$11/mo.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-primary/30 bg-primary/5 px-3.5 py-3 text-sm" data-testid="elevenreader-referral-note">
          <p className="font-semibold text-primary">Save 25% with this link</p>
          <p className="text-muted-foreground mt-0.5">
            Sign up through the button below and you'll get 25% off your first Ultra plan.
          </p>
        </div>

        <ol className="text-sm text-muted-foreground space-y-2 mt-1 list-decimal list-inside">
          <li>Download your book as an EPUB.</li>
          <li>Open ElevenReader and upload the EPUB.</li>
          <li>Press play — ElevenReader narrates it for you.</li>
        </ol>

        <div className="flex flex-col gap-2.5 mt-2">
          <Button
            variant="outline"
            onClick={handleDownload}
            disabled={downloading}
            data-testid="elevenreader-download-epub"
            className="w-full gap-2"
          >
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : done ? <Check className="w-4 h-4 text-primary" /> : <Download className="w-4 h-4" />}
            {done ? "EPUB downloaded — ready to upload" : "Download EPUB (to upload)"}
          </Button>
          <Button
            asChild
            data-testid="elevenreader-open"
            className="w-full gap-2 font-semibold"
          >
            <a href={REFERRAL_URL} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="w-4 h-4" /> Open ElevenReader &amp; save 25%
            </a>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
